export interface TimeoutHandle {
  promise: Promise<never>;
  cancel: () => void;
  race<T>(...promises: Array<Promise<T>>): Promise<T>;
}

export interface TimeoutStats {
  /** Number of `timeout()` instances created. */
  created: number;
  /**
   * Number of times a timer fired after the handle had already been settled
   * (either canceled or the timer had already rejected). Without the dedupe
   * guard these would have re-rejected the timeout promise and triggered
   * V8's `Runtime_PromiseResolveAfterResolved` slow path.
   */
  suppressedLateFires: number;
}

const stats: TimeoutStats = {
  created: 0,
  suppressedLateFires: 0,
};

export function getTimeoutStats(): Readonly<TimeoutStats> {
  return { ...stats };
}

export function resetTimeoutStats(): void {
  stats.created = 0;
  stats.suppressedLateFires = 0;
}

let statsLoggerStarted = false;

/**
 * Optionally start a periodic logger that prints `getTimeoutStats()` to stderr
 * every `intervalMs`. Gated behind explicit opt-in (caller passes the
 * interval). Idempotent — repeat calls are no-ops.
 */
export function startTimeoutStatsLogger(intervalMs: number): void {
  if (statsLoggerStarted) return;
  statsLoggerStarted = true;

  const timer = setInterval(() => {
    const snapshot = getTimeoutStats();

    // eslint-disable-next-line no-console
    console.warn(
      `[timeout-stats] created=${snapshot.created} suppressedLateFires=${snapshot.suppressedLateFires}`,
    );
  }, intervalMs);

  if (typeof timer.unref === 'function') {
    timer.unref();
  }
}

export function timeout(
  ms: number,
  message: string = `timeout of ${ms}ms reached`,
): TimeoutHandle {
  stats.created += 1;
  const error = new Error(message);
  let settled = false;
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

  const promise = new Promise<never>((_resolve, reject) => {
    if (settled) {
      return;
    }

    timeoutHandle = setTimeout(() => {
      if (settled) {
        stats.suppressedLateFires += 1;

        return;
      }

      settled = true;
      reject(error);
    }, ms);
  });

  const cancel = () => {
    if (settled) {
      return;
    }

    settled = true;

    if (timeoutHandle !== undefined) {
      clearTimeout(timeoutHandle);
    }
  };

  return {
    cancel,
    promise,
    race: async <T>(...promises: Array<Promise<T>>): Promise<T> => {
      try {
        return await Promise.race([...promises, promise]);
      } finally {
        cancel();
      }
    },
  };
}
